import { bus, EVENTS } from '../lib/eventBus';

/**
 * VocalizerAgent — speaks queued phrases via Web Speech API.
 *
 * Listens to phrase:ready, emits speaking:start/end, supports mute & rate/pitch/volume.
 */
export class VocalizerAgent {
  constructor() {
    this.queue = [];
    this.speaking = false;
    this.muted = false;
    this.rate = 1.0;
    this.pitch = 1.0;
    this.volume = 1.0;
    this.lang = 'en-US';
    this.synth = typeof window !== 'undefined' && 'speechSynthesis' in window
      ? window.speechSynthesis
      : null;
    this.unsubscribe = bus.on(EVENTS.PHRASE_READY, (p) => this._enqueue(p));
  }

  /** iOS Safari requires a user-gesture-triggered speak() before voices unlock. */
  unlock() {
    if (!this.synth) return;
    try {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      u.rate = 1;
      this.synth.speak(u);
    } catch {
      /* noop */
    }
  }

  _enqueue(payload) {
    if (!this.synth || this.muted) return;
    this.queue.push(payload);
    if (!this.speaking) this._dequeue();
  }

  _dequeue() {
    const next = this.queue.shift();
    if (!next) {
      this.speaking = false;
      return;
    }
    const u = new SpeechSynthesisUtterance(next.text);
    u.lang = this.lang || 'en-US';
    u.rate = this.rate;
    u.pitch = this.pitch;
    u.volume = this.volume;
    u.onstart = () => {
      this.speaking = true;
      bus.emit(EVENTS.SPEAKING_START, { text: next.text });
    };
    u.onend = () => {
      bus.emit(EVENTS.SPEAKING_END, { text: next.text });
      this._dequeue();
    };
    u.onerror = () => {
      bus.emit(EVENTS.SPEAKING_END, { text: next.text });
      this._dequeue();
    };
    this.synth.speak(u);
  }

  setMuted(muted) {
    this.muted = muted;
    if (muted) {
      this.queue = [];
      try {
        this.synth?.cancel();
      } catch {
        /* noop */
      }
      // synth.cancel triggers onerror which dequeues, but force the flag:
      this.speaking = false;
    }
  }

  setLang(lang) { this.lang = lang || 'en-US'; }
  setRate(v) { this.rate = v; }
  setPitch(v) { this.pitch = v; }
  setVolume(v) { this.volume = v; }

  destroy() {
    this.unsubscribe?.();
    try {
      this.synth?.cancel();
    } catch {
      /* noop */
    }
    this.queue = [];
    this.speaking = false;
  }
}
